import { BorderBeamTrace, MagneticPull, MotionStagger, MotionStaggerItem, WordReveal } from '../components/motion';
import { PageMeta } from '../components/PageMeta';
import { ScrollReveal } from '../components/ScrollReveal';
import { Band, Btn, Container, Eyebrow, SectionHead, Split } from '../site/primitives';
import { AstronautArt, Checklist, CustomerEssentials, FinalCTA } from '../site/sections';
import { ART } from '../site/media';
import { Status } from '../site/status';
import { motion, useReducedMotion } from 'framer-motion';
import { publicMotion, publicTransition } from '../lib/motion';

const values = [
  { icon: 'shield-check' as const, title: 'Ease of Mind', body: 'Delivery issues happen. Enshield covers every order until it reaches your customer, giving you and your customers peace of mind. We handle lost, stolen, and damaged package claims for you, so your team doesn’t have to. Our quick, straightforward process keeps customer service out of the claims cycle, protects your revenue, and helps turn issues into repeat purchases.' },
  { icon: 'plug' as const, title: 'Integration', body: 'Integrating Enshield into your website is simple. Our service helps merchants of any size offer Package Protection at checkout, turning a basic add-on into a customer experience tool.' },
  { icon: 'chart' as const, title: 'Turning Risk into Profit', body: 'Enshield turns your checkout into a profit center. Enshield also handles all claims, so there’s no added work for your team. The result is letting you monetize risk instead of absorbing it.' },
];

export function HomePage() {
  const reduced = Boolean(useReducedMotion());
  const slots = { hidden: reduced ? {} : { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: publicTransition(reduced, publicMotion.duration.scene) } };
  return <>
    <PageMeta title="Package Protection made simple" description="Enshield brings Package Protection, claims handling, and customer support together in one seamless experience." />
    <motion.section className="astra-hero" aria-labelledby="home-title" data-motion="hero-scene" initial={reduced ? false : 'hidden'} animate="show" variants={{ hidden: {}, show: { transition: { staggerChildren: reduced ? 0 : publicMotion.stagger.hero } } }}>
      <Container>
        <div className="astra-hero__grid">
          <div className="astra-hero__copy" data-motion="hero-copy">
            <Eyebrow>Package Protection. Personally handled.</Eyebrow>
            <motion.h1 variants={slots} data-motion="hero-headline" id="home-title"><WordReveal lines={['Redefining the standard', 'in Package Protection']} /></motion.h1>
            <motion.p variants={slots} data-motion="hero-support" className="u-lead">Enshield brings Package Protection, claims handling, and customer support together in one seamless experience.</motion.p>
            <motion.div variants={slots} data-motion="hero-actions" className="u-cluster"><MagneticPull><Btn to="/book-a-call">Book a Call</Btn></MagneticPull><Btn tone="ghost" to="/how-it-works">Explore how it works</Btn></motion.div>
          </div>
          <div className="astra-hero__art" data-motion="hero-artwork">
            <BorderBeamTrace duration={publicMotion.duration.ambient} />
            <AstronautArt {...ART.delivery} drift priority />
            <div className="astra-hero__caption"><strong>Protection beyond checkout.</strong><span>From your store to their door.</span></div>
          </div>
        </div>
        <div className="astra-service-strip" aria-label="The Enshield service"><span><b>01</b> Protection for your customers</span><span><b>02</b> Claims handled by Enshield</span><span><b>03</b> Visibility for your business</span></div>
      </Container>
    </motion.section>
    <Band space="spacious" labelledBy="home-value-title">
      <ScrollReveal className="astra-values">
        <div className="astra-values__intro">
          <SectionHead align="start" eyebrow="Why Enshield" titleId="home-value-title" title="Protection & Profit Made Simple" lead="Learn how Enshield helps merchants protect shipments, reduce refunds, improve customer experience, and add incremental profit." />
          <div className="astra-values__guide"><AstronautArt {...ART.shield} support /></div>
        </div>
        <MotionStagger className="astra-values__rows">{values.map((value, index) => <MotionStaggerItem key={value.title}><article className="astra-values__row"><span>0{index + 1}</span><div><h3>{value.title}</h3><p>{value.body}</p></div></article></MotionStaggerItem>)}</MotionStagger>
      </ScrollReveal>
    </Band>
    <Band space="spacious" tone="alt" labelledBy="home-claims-title">
      <Split weight="copy">
        <ScrollReveal className="u-stack u-stack--lg">
          <SectionHead
            eyebrow="Claims, handled"
            titleId="home-claims-title"
            title="We take the claim off your team’s plate."
            lead="When a protected delivery is lost, stolen, or damaged, your customer works directly with Enshield. We verify the order, review the claim, and manage the resolution from start to finish."
          />
          <Checklist items={[
            'Customers file directly with Enshield',
            'We verify protected orders and claim eligibility',
            'Enshield reviews and resolves the claim',
            'Your customer receives a direct path to replace the item',
            'Your team stays out of the claims cycle',
          ]} />
          <div className="u-cluster">
            <Btn tone="secondary" to="/how-it-works">Learn How It Works</Btn>
          </div>
        </ScrollReveal>
        <ScrollReveal delay={0.08}>
          <div className="p-frame astra-story-art">
                <AstronautArt {...ART.operations} support />
                <div className="astra-story-art__steps" aria-label="Managed claim journey">
                  <Status name="protected" size="lg" />
                  <Status name="submitted" size="lg" label="Claim submitted" />
                  <Status name="in-review" size="lg" label="Reviewed" />
                  <Status name="resolved" size="lg" label="Approved" />
                  <Status name="resolved" size="lg" />
                </div>
          </div>
        </ScrollReveal>
      </Split>
    </Band>
    <CustomerEssentials />
    <FinalCTA signature titleId="home-cta-title" title="Ready to make Package Protection work for your business?" primary={{ label: 'Book a Call', to: '/book-a-call' }} />
  </>;
}
